import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletContext } from '../context/WalletContext';
import { PublicKey, VersionedTransaction, TransactionMessage } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { useUser } from '@civic/auth-web3/react';
import { CONFIG } from '../config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { FiArrowLeft, FiDollarSign, FiTrendingUp, FiShield, FiZap } from 'react-icons/fi';
import { logger } from '../utils/logger';
import { useAuth } from '../hooks/useAuth';
import { recordDeposit, recordWithdrawal } from '../utils/transactionDb';

// Constants
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');

// Cache constants
const CACHE_KEY = 'lulo_pools_data';
const CACHE_TIMESTAMP_KEY = 'lulo_pools_timestamp';
const CACHE_DURATION = 20 * 60 * 1000; // 20 minutes in milliseconds

function LuloPools() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [usdcBalance, setUsdcBalance] = useState(0);
  const [metrics, setMetrics] = useState({
    totalLiquidity: 0,
    availableLiquidity: 0,
    regularLiquidityAmount: 0,
    protectedLiquidityAmount: 0,
    protectedApy: '0.00',
  });
  const [rates, setRates] = useState({
    CURRENT: 0,
    '1HR': 0,
    '24HR': 0,
    '7DAY': 0,
    '30DAY': 0,
    '1YR': 0
  });
  const [loadingRates, setLoadingRates] = useState(true);
  const navigate = useNavigate();
  const { wallet } = useWalletContext();
  const userContext = useUser();
  const civicWallet = userContext?.solana?.wallet;
  const [balances, setBalances] = useState({ USDC: '0.00' });
  const { email } = useAuth();

  // Function to fetch data from API
  const fetchPoolsData = async () => {
    try {
      const options = {
        method: 'GET',
        headers: {
          'x-api-key': CONFIG.LULO_API_KEY,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch('https://api.lulo.fi/v1/pool.getPools', options);
      const data = await response.json();
      
      // Debug: Log the API response structure
      logger.log('API Response:', JSON.stringify(data, null, 2));
      logger.log('Protected data:', data?.protected);
      logger.log('Protected APY:', data?.protected?.apy);

      // Extract metrics from the response
      const protectedApy = data?.protected?.apy !== undefined 
        ? (parseFloat(data.protected.apy) * 100).toFixed(2) 
        : '0.00';
        
      logger.log('Formatted Protected APY:', protectedApy);
      
      const metrics = {
        totalLiquidity: data.totalLiquidity || 0,
        availableLiquidity: data.availableLiquidity || 0,
        regularLiquidityAmount: data.regularLiquidityAmount || 0,
        protectedLiquidityAmount: data.protectedLiquidityAmount || 0,
        protectedApy: protectedApy
      };
      
      logger.log('Metrics to be set:', metrics);
      setMetrics(metrics);

      let poolsList = [];
      if (Array.isArray(data.pools)) {
        poolsList = data.pools;
      } else if (Array.isArray(data.data)) {
        poolsList = data.data;
      } else if (data.regular && data.protected) {
        poolsList = [data.regular, data.protected];
      } else if (Array.isArray(data)) {
        poolsList = data;
      } else {
        throw new Error('Unexpected response format');
      }

      // Store in local storage
      const cacheData = {
        pools: poolsList,
        metrics,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());

      setPools(poolsList);
      setLoading(false);
    } catch (err) {
      logger.error('Error fetching pools data:', err);
      setError(err.message);
      setShowErrorToast(true);
      setLoading(false);
    }
  };

  // Function to load data from cache
  const loadFromCache = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cachedTimestamp) {
        const data = JSON.parse(cachedData);
        const timestamp = parseInt(cachedTimestamp);
        const now = Date.now();
        
        if (now - timestamp < CACHE_DURATION) {
          // Cache is still valid
          setPools(data.pools);
          setMetrics(data.metrics);
          setLoading(false);
          return true;
        }
      }
      return false;
    } catch (err) {
      logger.error('Error loading from cache:', err);
      return false;
    }
  };

  // Fetch rates data
  const fetchRates = async () => {
    try {
      setLoadingRates(true);
      const options = {
        method: 'GET',
        headers: {
          'x-api-key': CONFIG.LULO_API_KEY,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch('https://api.lulo.fi/v1/rates.getRates', options);
      const data = await response.json();
      
      if (data.protected) {
        setRates(data.protected);
      }
    } catch (err) {
      logger.error('Error fetching rates:', err);
      setError('Failed to load rates data');
      setShowErrorToast(true);
    } finally {
      setLoadingRates(false);
    }
  };

  // Function to fetch protected pool APY
  const fetchProtectedPoolApy = async () => {
    try {
      const options = {
        method: 'GET',
        headers: {
          'x-api-key': CONFIG.LULO_API_KEY,
          'Content-Type': 'application/json',
        },
      };
      const response = await fetch('https://api.lulo.fi/v1/pool.getPools', options);
      const data = await response.json();
      
      if (data?.protected?.apy !== undefined) {
        const apy = (parseFloat(data.protected.apy) * 100).toFixed(2);
        setMetrics(prev => ({
          ...prev,
          protectedApy: apy
        }));
      }
    } catch (err) {
      logger.error('Error fetching protected pool APY:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (!loadFromCache()) {
        await fetchPoolsData();
      }
      await fetchRates();
      await fetchProtectedPoolApy();
    };
    loadData();
  }, []);

  // Set up periodic refresh
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      await fetchPoolsData();
    }, CACHE_DURATION);

    return () => clearInterval(refreshInterval);
  }, []);

  // Fetch USDC balance for connected wallet
  useEffect(() => {
    const fetchUSDCBalance = async () => {
      if (!civicWallet?.publicKey) {
        setUsdcBalance(0);
        return;
      }
      
      try {
        const ownerPubkey = civicWallet.publicKey;
        const resp = await rpc.getParsedTokenAccountsByOwner(
          ownerPubkey, 
          { programId: TOKEN_PROGRAM_ID }
        );
        
        let usdcAmt = 0;
        resp.value.forEach(({ account }) => {
          const info = account.data.parsed.info;
          const mint = info.mint;
          const amount = info.tokenAmount?.uiAmount || 0;
          if (mint === USDC_MINT.toBase58()) usdcAmt = amount;
        });
        
        setUsdcBalance(usdcAmt);
      } catch (err) {
        logger.error('Error fetching USDC balance:', err);
        setUsdcBalance(0);
      }
    };
    
    fetchUSDCBalance();
  }, [civicWallet?.publicKey]);

  // Error toast close handler
  const handleCloseToast = () => setShowErrorToast(false);

  const FEE_PERCENTAGE = 0.5; // 0.5% fee
  const FEE_WALLET = 'GjG2o2KXikkfEDBz1a8NAtgXrzXuYvK5GAMxnGXvFHzU';

  // Function to get USDC balance
  const getUSDCBalance = async (walletAddress) => {
    try {
      const tokenAccounts = await rpc.getParsedTokenAccountsByOwner(
        walletAddress,
        { programId: TOKEN_PROGRAM_ID }
      );

      // Find USDC token account
      const usdcAccount = tokenAccounts.value.find(account => 
        account.account.data.parsed.info.mint === USDC_MINT.toBase58()
      );

      // If no USDC account, balance is 0
      if (!usdcAccount) return 0;

      // Return balance in USDC (6 decimals)
      return usdcAccount.account.data.parsed.info.tokenAmount.uiAmount;
    } catch (error) {
      logger.error('Error fetching USDC balance:', error);
      return 0;
    }
  };

  const handleDeposit = async (pool, amount) => {
    if (!civicWallet?.publicKey || !wallet?.publicKey) {
      setError('Please connect your wallet first');
      setShowErrorToast(true);
      return;
    }

    // Use the exact amount entered by the user
    const depositAmount = amount; // Use the exact amount from input
    const feeAmount = (depositAmount * 0.5) / 100; // 0.5% fee
    const totalAmount = depositAmount + feeAmount;

    // Check USDC balance before proceeding
    const usdcBalance = await getUSDCBalance(civicWallet.publicKey);
    
    if (usdcBalance < totalAmount) {
      const shortBy = (totalAmount - usdcBalance).toFixed(6);
      throw new Error(`Insufficient USDC balance. You need ${shortBy} more USDC to complete this transaction.`);
    }

    try {
      setLoading(true);
      setError(null);

      // Calculate fee (0.5% of the deposit amount)
      const feeAmount = (amount * FEE_PERCENTAGE) / 100;
      const amountAfterFee = amount - feeAmount;
      
      if (amountAfterFee <= 0) {
        throw new Error('Amount after fee is too small');
      }

      // Use the exact user-entered amount; Lulo will handle fee via feeAmount
      const depositAmount = amount;
      const feeInLamports = feeAmount;

      // Step 1: POST to Lulo API to generate the transaction
      const response = await fetch('https://api.lulo.fi/v1/generate.transactions.deposit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': CONFIG.LULO_API_KEY
        },
        body: JSON.stringify({
          owner: civicWallet.publicKey.toBase58(),
          feePayer: civicWallet.publicKey.toBase58(),
          mintAddress: USDC_MINT.toBase58(),
          regularAmount: pool.type === 'regular' ? depositAmount : 0,
          protectedAmount: pool.type === 'protected' ? depositAmount : 0,
          feeAmount: feeInLamports,
          feeDestination: FEE_WALLET
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('Lulo API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error?.message || 'Failed to generate deposit transaction');
      }

      const { transaction: txData } = await response.json();
      
      // Deserialize the transaction
      const transactionBuffer = Buffer.from(txData, 'base64');
      const parsedTransaction = VersionedTransaction.deserialize(transactionBuffer);
      
      // Fetch ALT accounts if present to allow decompile/compile
      let addressLookupTableAccounts = [];
      try {
        if (parsedTransaction.message.addressTableLookups?.length) {
          const lookups = parsedTransaction.message.addressTableLookups;
          addressLookupTableAccounts = await Promise.all(
            lookups.map(async (l) => {
              const res = await rpc.getAddressLookupTable(new PublicKey(l.accountKey));
              return res.value;
            })
          );
          addressLookupTableAccounts = addressLookupTableAccounts.filter(Boolean);
        }
      } catch (altErr) {
        logger.error('Failed to fetch address lookup tables:', altErr);
      }
      
      // Helper function to sign transaction with wallet (prefer Civic wallet like RegularPool)
      const signTransactionWithWallet = async (transaction) => {
        try {
          if (civicWallet && typeof civicWallet.signTransaction === 'function') {
            return await civicWallet.signTransaction(transaction);
          }
          if (wallet && typeof wallet.signTransaction === 'function') {
            return await wallet.signTransaction(transaction);
          }
          throw new Error('No supported transaction signing method found');
        } catch (error) {
          logger.error('Error signing transaction:', error);
          throw new Error(`Failed to sign transaction: ${error.message}`);
        }
      };

      // Get fresh blockhash and last valid block height
      const { blockhash: latestBlockhash, lastValidBlockHeight } = await rpc.getLatestBlockhash('confirmed');
      
      // Decompile and rebuild with fresh blockhash (v0 message)
      const decompiled = TransactionMessage.decompile(parsedTransaction.message, { addressLookupTableAccounts });
      if (!decompiled.payerKey) decompiled.payerKey = civicWallet.publicKey;
      decompiled.recentBlockhash = latestBlockhash;
      const v0msg = decompiled.compileToV0Message(addressLookupTableAccounts);
      const depositTx = new VersionedTransaction(v0msg);
      
      // Sign (locally) and send via RPC (mirrors RegularPool)
      let signature;
      try {
        logger.log('Signing transaction with wallet...');
        const signedTx = await signTransactionWithWallet(depositTx);
        logger.log('Sending transaction with fresh blockhash...');
        signature = await rpc.sendRawTransaction(
          signedTx.serialize(),
          { 
            skipPreflight: false, 
            maxRetries: 5,
            preflightCommitment: 'confirmed'
          }
        );
        
        // Wait for confirmation with the correct last valid block height
        logger.log('Waiting for transaction confirmation...');
        const confirmation = await rpc.confirmTransaction(
          {
            signature,
            blockhash: latestBlockhash,
            lastValidBlockHeight,
          },
          'confirmed'
        );
        
        if (confirmation.value.err) {
          throw new Error('Transaction failed: ' + (confirmation.value.err.toString() || 'Unknown error'));
        }
      } catch (error) {
        logger.error('Transaction error:', error);
        throw new Error(`Transaction failed: ${error.message}`);
      }

      // Show success modal
      setTransactionResult({
        show: true,
        isSuccess: true,
        title: 'Deposit Successful',
        message: `You have successfully deposited ${depositAmount} USDC to the protected pool.`,
        txSignature: signature
      });
      
      // Record the deposit in our database (non-blocking for UX)
      try {
        const walletAddress = civicWallet.publicKey.toBase58();
        const userId = email || walletAddress; // prefer email if available
        await recordDeposit(userId, walletAddress, 'protected', depositAmount, signature);
        logger.log('Protected pool deposit recorded in database');
      } catch (dbErr) {
        logger.error('Error recording protected deposit in database:', dbErr);
        // Do not interrupt UX if DB recording fails
      }
      setShowDepositModal(false);
      // Refresh pool data
      window.location.reload();
    } catch (err) {
      logger.error('Lulo deposit error:', err);
      
      // Check for specific error patterns and provide user-friendly messages
      let errorMessage = 'Failed to process deposit';
      
      if (err.message.includes('insufficient funds') || err.message.includes('0x1')) {
        errorMessage = 'Insufficient USDC balance to complete this deposit';
      } else if (err.message.includes('Transaction simulation failed')) {
        // Simulation errors often contain detailed error information
        if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient balance to complete this transaction';
        } else if (err.message.includes('0x1')) {
          errorMessage = 'Insufficient liquidity in the pool. Please try a smaller amount or check back later.';
        } else {
          errorMessage = 'Transaction simulation failed. Please try again or contact support if the issue persists.';
        }
      } else if (err.message) {
        // For other errors, use the message but clean it up if it's too technical
        errorMessage = err.message
          .replace('Error: ', '')
          .replace('Transaction simulation failed: ', '')
          .replace(/Logs: \[.*\]/g, '') // Remove raw logs from error message
          .replace(/Simulation failed\. Message: /, '') // Remove simulation prefix
          .replace(/Catch the `.*` and call `.*` on it for full details\.?/g, ''); // Remove technical details
      }
      
      setError(errorMessage);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (amount) => {
    if (!civicWallet?.publicKey) {
      setError('Please connect your wallet first');
      setShowErrorToast(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the exact amount without conversion to lamports
      const withdrawAmount = amount;
      logger.log('Initiating withdrawal of', withdrawAmount, 'USDC from protected pool');

      // Call the Lulo API to generate withdrawal instructions for protected pool
      const response = await fetch('https://api.lulo.fi/v1/generate.transactions.withdrawProtected', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': CONFIG.LULO_API_KEY
        },
        body: JSON.stringify({
          owner: civicWallet.publicKey.toBase58(),
          feePayer: civicWallet.publicKey.toBase58(),
          mintAddress: USDC_MINT.toBase58(),
          amount: withdrawAmount
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('Lulo API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error?.message || 'Failed to generate withdrawal transaction');
      }

      const responseData = await response.json();
      logger.log('Lulo API Response:', responseData);
      
      if (!responseData || !responseData.transaction) {
        throw new Error('Invalid response from Lulo API: Missing transaction data');
      }

      // Deserialize the transaction
      logger.log('Deserializing withdrawal transaction...');
      const transactionStr = responseData.transaction;
      if (typeof transactionStr !== 'string') {
        throw new Error(`Expected transaction to be a base64 string, got: ${typeof transactionStr}`);
      }
      
      const transactionBuffer = Buffer.from(transactionStr, 'base64');
      const deserializedTransaction = VersionedTransaction.deserialize(transactionBuffer);
      
      // Get fresh blockhash and last valid block height
      const { blockhash: latestBlockhash, lastValidBlockHeight } = await rpc.getLatestBlockhash('confirmed');
      
      // Fetch any required address lookup tables
      let addressLookupTableAccounts = [];
      if (deserializedTransaction.message.addressTableLookups?.length > 0) {
        const lookupPromises = deserializedTransaction.message.addressTableLookups.map(async (lookup) => {
          try {
            const result = await rpc.getAddressLookupTable(new PublicKey(lookup.accountKey));
            return result.value;
          } catch (error) {
            logger.warn('Failed to fetch address lookup table:', error);
            return null;
          }
        });
        
        const results = await Promise.all(lookupPromises);
        addressLookupTableAccounts = results.filter(Boolean);
      }
      
      // Decompile and rebuild with fresh blockhash (v0 message)
      const decompiled = TransactionMessage.decompile(deserializedTransaction.message, { addressLookupTableAccounts });
      if (!decompiled.payerKey) decompiled.payerKey = civicWallet.publicKey;
      decompiled.recentBlockhash = latestBlockhash;
      const v0msg = decompiled.compileToV0Message(addressLookupTableAccounts);
      const withdrawTx = new VersionedTransaction(v0msg);
      
      // Sign the transaction with the wallet
      logger.log('Signing transaction with fresh blockhash...');
      const signedTx = await signTransactionWithWallet(withdrawTx);
      
      // Send the transaction with retry logic
      logger.log('Sending transaction with fresh blockhash...');
      const signature = await sendTransactionWithRetry(signedTx);

      // Wait for confirmation with the correct last valid block height
      logger.log('Waiting for transaction confirmation...');
      const confirmation = await rpc.confirmTransaction(
        {
          signature,
          blockhash: latestBlockhash,
          lastValidBlockHeight,
        },
        'confirmed'
      );

      if (confirmation.value.err) {
        throw new Error('Transaction failed');
      }

      // Show success modal
      setTransactionResult({
        show: true,
        isSuccess: true,
        title: 'Withdrawal Successful',
        message: `You have successfully initiated a withdrawal of ${withdrawAmount} USDC from the protected pool.`,
        txSignature: signature
      });
      
      // Record the withdrawal in our database (non-blocking for UX)
      try {
        const walletAddress = civicWallet.publicKey.toBase58();
        const userId = email || walletAddress; // prefer email if available
        await recordWithdrawal(userId, walletAddress, 'protected', withdrawAmount, signature);
        logger.log('Protected pool withdrawal recorded in database');
      } catch (dbErr) {
        logger.error('Error recording protected withdrawal in database:', dbErr);
        // Do not interrupt UX if DB recording fails
      }
      setShowWithdrawalModal(false);
      
      // Refresh pool data
      window.location.reload();
    } catch (err) {
      logger.error('Lulo withdrawal error:', err);
      
      // Check for specific error patterns and provide user-friendly messages
      let errorMessage = 'Failed to process withdrawal';
      
      if (err.message.includes('insufficient funds')) {
        errorMessage = 'Insufficient USDC balance to complete this withdrawal';
      } else if (err.message.includes('custom program error: 0x1')) {
        // This is a common error for insufficient funds in the pool
        errorMessage = 'Insufficient liquidity in the pool. Please try a smaller amount or check back later.';
      } else if (err.message.includes('Transaction simulation failed')) {
        // Simulation errors often contain detailed error information
        if (err.message.includes('insufficient funds')) {
          errorMessage = 'Insufficient balance to complete this transaction';
        } else {
          errorMessage = 'Transaction simulation failed. Please try again or contact support if the issue persists.';
        }
      } else if (err.message) {
        // For other errors, use the message but clean it up if it's too technical
        errorMessage = err.message
          .replace('Error: ', '')
          .replace('Transaction simulation failed: ', '')
          .replace(/Logs: \[.*\]/g, ''); // Remove raw logs from error message
      }
      
      setError(errorMessage);
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    if (!depositAmount || isNaN(depositAmount) || parseFloat(depositAmount) <= 0) {
      setError('Please enter a valid amount');
      setShowErrorToast(true);
      return;
    }
    
    try {
      const pool = pools.find(p => p.type === 'protected');
      if (pool) {
        await handleDeposit(pool, parseFloat(depositAmount));
        setDepositAmount('');
      }
    } catch (err) {
      logger.error('Deposit error:', err);
      setError(err.message || 'Failed to process deposit');
      setShowErrorToast(true);
    }
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawAmount || isNaN(withdrawAmount) || parseFloat(withdrawAmount) <= 0) {
      setError('Please enter a valid amount');
      setShowErrorToast(true);
      return;
    }
    
    try {
      const pool = pools.find(p => p.type === 'protected');
      if (pool) {
        await handleWithdraw(pool, parseFloat(withdrawAmount));
        setWithdrawAmount('');
      }
    } catch (err) {
      logger.error('Withdraw error:', err);
      setError(err.message || 'Failed to process withdrawal');
      setShowErrorToast(true);
    }
  };

  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  
  // Transaction result modal state
  const [transactionResult, setTransactionResult] = useState({
    show: false,
    isSuccess: false,
    title: '',
    message: '',
    txSignature: ''
  });

  // Helper function to sign transaction with wallet
  const signTransactionWithWallet = async (transaction) => {
    try {
      if (!civicWallet?.signTransaction) {
        throw new Error('Wallet does not support signing transactions');
      }
      return await civicWallet.signTransaction(transaction);
    } catch (error) {
      logger.error('Error signing transaction:', error);
      throw new Error('Failed to sign transaction. Please try again.');
    }
  };

  // Helper function to send transaction with retry logic
  const sendTransactionWithRetry = async (signedTx, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const signature = await connection.sendRawTransaction(
          signedTx.serialize(),
          {
            skipPreflight: false,
            preflightCommitment: 'confirmed'
          }
        );
        return signature;
      } catch (error) {
        lastError = error;
        logger.warn(`Attempt ${attempt} failed:`, error);
        
        // If we have retries left, wait a bit before trying again
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Exponential backoff, max 5s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // If we get here, all retries failed
    logger.error('All retry attempts failed:', lastError);
    throw new Error(`Failed to send transaction after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  };

  // Handle max button click for deposit and withdraw
  const handleMaxDeposit = () => {
    setDepositAmount(usdcBalance.toFixed(2));
  };

  const handleMaxWithdraw = () => {
    // For withdrawal, you might want to show the available balance to withdraw
    // For now, we'll use the same as deposit
    setWithdrawAmount(usdcBalance.toFixed(2));
  };

  const formatAPY = (apy) => {
    if (apy === undefined || apy === null) return '0.00%';
    return `${Number(apy).toFixed(2)}%`;
  };

  return (
    <div className="pools-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
      {/* Error Toast */}
      {showErrorToast && error && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: '#EF4444',
          color: 'white',
          padding: '16px 20px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: '90%',
          width: '400px',
          animation: 'fadeIn 0.3s ease-out',
          borderLeft: '4px solid #DC2626',
          overflow: 'hidden'
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V12M12 15H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: '4px',
              fontSize: '0.95em'
            }}>
              Error
            </div>
            <div style={{ 
              fontSize: '0.9em', 
              opacity: 0.9,
              lineHeight: 1.3
            }}>
              {error}
            </div>
          </div>
          <button 
            onClick={handleCloseToast}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              opacity: 0.7,
              padding: '2px',
              borderRadius: '4px',
              transition: 'all 0.2s',
              alignSelf: 'flex-start',
              ':hover': {
                opacity: 1,
                background: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button 
          onClick={() => navigate('/pools')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '8px 16px',
            color: '#4a5568',
            cursor: 'pointer',
            marginBottom: '1.5rem',
            transition: 'all 0.2s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.borderColor = '#cbd5e0';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = '#e2e8f0';
          }}
        >
          <FiArrowLeft size={18} />
          <span>Back to Pools</span>
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#1a202c', margin: 0 }}>Protected Pool</h1>
          <div style={{
            background: '#ebf8ff',
            color: '#3182ce',
            padding: '8px 16px',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FiShield size={18} />
            <span>Protected Pool</span>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
          marginBottom: '2rem',
          border: '1px solid #e2e8f0'
        }}>
          <p style={{ color: '#4a5568', marginBottom: '1.5rem', fontSize: '1.1rem', lineHeight: 1.6 }}>
            Risk-protected deposits with coverage for protocol failures. Your funds are protected against smart contract vulnerabilities and other risks.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginTop: '2rem'
          }}>
            <MetricCard 
              icon={<FiTrendingUp size={24} />}
              title="Protected APY"
              value={`${metrics.protectedApy}%`}
              color="#8b5cf6"
            />
            <MetricCard 
              icon={<FiDollarSign size={24} />}
              title="Total Liquidity"
              value={formatUSDC(metrics.totalLiquidity)}
              color="#4f46e5"
            />
            <MetricCard 
              icon={<FiTrendingUp size={24} />}
              title="Available Liquidity"
              value={formatUSDC(metrics.availableLiquidity)}
              color="#10b981"
            />
          </div>
        </div>
      </div>

      {/* APY Performance Chart */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '1.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
        marginBottom: '2rem',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1a202c', margin: 0 }}>APY Performance</h2>
          <div style={{
            background: '#f8fafc',
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '0.875rem',
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>Updated just now</span>
          </div>
        </div>

        {loadingRates ? (
          <div style={{
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            fontSize: '1.1rem'
          }}>
            Loading APY data...
          </div>
        ) : (
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={[
                  { name: 'Current', rate: rates.CURRENT },
                  { name: '1H', rate: rates['1HR'] },
                  { name: '24H', rate: rates['24HR'] },
                  { name: '7D', rate: rates['7DAY'] },
                  { name: '30D', rate: rates['30DAY'] },
                  { name: '1Y', rate: rates['1YR'] },
                ]}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.01}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b' }}
                  width={40}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  formatter={(value) => [`${Number(value).toFixed(2)}%`, 'APY']}
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  labelStyle={{ color: '#4f46e5', fontWeight: 600 }}
                />
                <Area 
                  type="monotone" 
                  dataKey="rate" 
                  stroke="#4f46e5" 
                  fillOpacity={1} 
                  fill="url(#colorRate)" 
                  strokeWidth={2}
                  dot={{
                    fill: '#4f46e5',
                    stroke: '#fff',
                    strokeWidth: 2,
                    r: 4,
                    fillOpacity: 1
                  }}
                  activeDot={{
                    fill: '#4f46e5',
                    stroke: '#fff',
                    strokeWidth: 2,
                    r: 6
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Balance and Action Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Your Balances Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a202c', margin: '0 0 1.5rem 0' }}>Your Balance</h3>
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ color: '#64748b' }}>USDC Balance</span>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{usdcBalance.toFixed(2)} USDC</span>
            </div>
          </div>
        </div>

        {/* Deposit Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a202c', margin: '0 0 1.5rem 0' }}>Deposit USDC</h3>
          <form onSubmit={handleDepositSubmit}>
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="0.00"
                      style={{
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        fontSize: '1rem',
                        color: '#1e293b',
                        background: 'transparent',
                        paddingRight: '120px'
                      }}
                    />
                    <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', whiteSpace: 'nowrap', marginRight: '8px' }}>USDC</span>
                      <button
                        type="button"
                        onClick={handleMaxDeposit}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          color: '#4f46e5',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#e2e8f0'}
                        onMouseOut={(e) => e.target.style.background = '#f1f5f9'}
                      >
                        MAX
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !civicWallet?.publicKey || !depositAmount || parseFloat(depositAmount) <= 0}
                style={{
                  width: '100%',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  opacity: (loading || !civicWallet?.publicKey || !depositAmount || parseFloat(depositAmount) <= 0) ? 0.7 : 1,
                  pointerEvents: (loading || !civicWallet?.publicKey || !depositAmount || parseFloat(depositAmount) <= 0) ? 'none' : 'auto'
                }}
                onMouseOver={(e) => !loading && civicWallet?.publicKey && depositAmount && parseFloat(depositAmount) > 0 && (e.target.style.background = '#e2e8f0')}
                onMouseOut={(e) => !loading && (e.target.style.background = '#f1f5f9')}
              >
                {loading ? 'Processing...' : 'Deposit'}
              </button>
            </div>
          </form>
        </div>

        {/* Withdraw Card */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a202c', margin: '0 0 1.5rem 0' }}>Withdraw USDC from Protected Pool</h3>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            if (withdrawAmount && parseFloat(withdrawAmount) > 0) {
              handleWithdraw(parseFloat(withdrawAmount));
            }
          }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '0.75rem 1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="0.00"
                      style={{
                        border: 'none',
                        outline: 'none',
                        width: '100%',
                        fontSize: '1rem',
                        color: '#1e293b',
                        background: 'transparent',
                        paddingRight: '120px'
                      }}
                    />
                    <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                      <span style={{ color: '#64748b', whiteSpace: 'nowrap', marginRight: '8px' }}>USDC</span>
                      <button
                        type="button"
                        onClick={handleMaxWithdraw}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          color: '#4f46e5',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#e2e8f0'}
                        onMouseOut={(e) => e.target.style.background = '#f1f5f9'}
                      >
                        MAX
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !civicWallet?.publicKey || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                style={{
                  width: '100%',
                  background: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  opacity: (loading || !civicWallet?.publicKey || !withdrawAmount || parseFloat(withdrawAmount) <= 0) ? 0.7 : 1,
                  pointerEvents: (loading || !civicWallet?.publicKey || !withdrawAmount || parseFloat(withdrawAmount) <= 0) ? 'none' : 'auto'
                }}
                onMouseOver={(e) => !loading && civicWallet?.publicKey && withdrawAmount && parseFloat(withdrawAmount) > 0 && (e.target.style.background = '#4338ca')}
                onMouseOut={(e) => !loading && (e.target.style.background = '#4f46e5')}
              >
                {loading ? 'Processing...' : 'Withdraw'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Transaction Result Modal */}
      {transactionResult.show && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: transactionResult.isSuccess ? '#10B981' : '#EF4444',
          color: 'white',
          padding: '16px 20px',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: '90%',
          width: '400px',
          animation: 'fadeIn 0.3s ease-out',
          borderLeft: `4px solid ${transactionResult.isSuccess ? '#059669' : '#DC2626'}`,
          overflow: 'hidden',
          '@media (max-width: 480px)': {
            width: '90%',
            padding: '14px 16px',
            maxWidth: 'calc(100% - 32px)'
          }
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px'
          }}>
            {transactionResult.isSuccess ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
              </svg>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: '4px',
              fontSize: '0.95em'
            }}>
              {transactionResult.title}
            </div>
            <div style={{ 
              fontSize: '0.9em', 
              opacity: 0.9,
              lineHeight: 1.3
            }}>
              {transactionResult.message}
            </div>
              
              {transactionResult.txSignature && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '0.8em',
                  opacity: 0.8,
                  wordBreak: 'break-all'
                }}>
                  TX: {transactionResult.txSignature}
                </div>
              )}
              
            <button 
              onClick={() => setTransactionResult({...transactionResult, show: false})}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                opacity: 0.7,
                padding: '2px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                alignSelf: 'flex-start',
                marginLeft: '8px',
                flexShrink: 0,
                ':hover': {
                  opacity: 1,
                  background: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function formatUSDC(amount) {
  if (amount === undefined || amount === null) return '$0.00';
  return '$' + Number(amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

// --- UI Components ---
const MetricCard = ({ icon, title, value, color }) => {
  if (value === undefined || value === null) return '-';
  return (
    <div style={{
      background: '#f8fafc',
      borderRadius: '12px',
      padding: '1.25rem',
      borderLeft: `4px solid ${color}`
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '0.75rem',
        color: color
      }}>
        {icon}
      </div>
      <h3 style={{
        fontSize: '0.875rem',
        color: '#64748b',
        margin: '0 0 0.25rem 0',
        fontWeight: 500
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#1e293b',
        margin: 0
      }}>
        {value}
      </p>
    </div>
  );
};

export default LuloPools;